import {
    AcademicCapIcon,
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    BriefcaseIcon,
    CalendarDaysIcon,
    ChartBarIcon,
    CheckIcon,
    ClipboardDocumentIcon,
    CurrencyDollarIcon,
    EyeIcon,
    EyeSlashIcon,
    EnvelopeIcon,
    HomeIcon,
    KeyIcon,
    LockClosedIcon,
    PresentationChartLineIcon,
    QrCodeIcon,
    ReceiptPercentIcon,
    ShareIcon,
    UserCircleIcon,
    UserGroupIcon,
    UsersIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { api } from '../lib/api';

const allNavigationItems = [
  { name: 'Dashboard',            href: '/dashboard',  icon: HomeIcon,          roles: ['admin', 'counselor'] },
  { name: 'CRM Management',       href: '/crm',        icon: UsersIcon,          roles: ['admin', 'counselor'] },
  { name: 'Student Management',   href: '/students',   icon: UserGroupIcon,      roles: ['admin', 'counselor'] },
  { name: 'Attendance Management',href: '/attendance', icon: CalendarDaysIcon,   roles: ['admin', 'counselor', 'trainer'] },
  { name: 'Course Management',    href: '/courses',    icon: AcademicCapIcon,    roles: ['admin', 'counselor'] },
  { name: 'Staff Management',     href: '/staff',      icon: BriefcaseIcon,      roles: ['admin'] },
  { name: 'Fee Management',       href: '/fees',       icon: CurrencyDollarIcon, roles: ['admin', 'counselor'] },
  { name: 'Trainer Management',   href: '/trainer',    icon: UserCircleIcon,     roles: ['admin', 'counselor'] },
  { name: 'Batch Analytics',      href: '/analytics',         icon: ChartBarIcon,              roles: ['admin'] },
  { name: 'Staff Analytics',      href: '/staff-analytics',   icon: PresentationChartLineIcon, roles: ['admin'] },
  { name: 'Expense Tracker',      href: '/expenses',          icon: ReceiptPercentIcon,        roles: ['admin'] },
];

// The enquiry URL — always absolute so the QR code and email contain a proper link
const ENQUIRY_URL = `${window.location.origin}/enquiry`;

// Shimmer effect for skeleton loading
const Shimmer = () => (
  <motion.div
    className="absolute inset-0 -translate-x-full"
    style={{
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
    }}
    animate={{
      x: ['0%', '200%'],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
);

// Sidebar skeleton loader
const SidebarSkeleton = () => (
  <div className="h-screen w-64 bg-gradient-to-b from-[#f8f7ff] to-[#f0ebff] shadow-xl relative overflow-hidden border-r border-purple-100">
    <div className="h-16 px-6 border-b border-purple-100 flex items-center relative z-10">
      <div className="relative overflow-hidden bg-purple-100 rounded h-8 w-32">
        <Shimmer />
      </div>
    </div>

    <div className="mt-8 px-4 space-y-2 relative z-10">
      {[...Array(6)].map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1, duration: 0.3 }}
          className="relative overflow-hidden bg-purple-100 rounded h-12 w-full"
        >
          <Shimmer />
        </motion.div>
      ))}
    </div>

    <div className="absolute bottom-6 left-4 right-4 z-10">
      <div className="relative overflow-hidden bg-red-100 rounded-lg h-10 w-full">
        <Shimmer />
      </div>
    </div>
  </div>
);

// Header skeleton loader
const HeaderSkeleton = () => (
  <header className="bg-white shadow-sm border-b border-purple-100 h-16 relative overflow-hidden">
    <div className="flex items-center justify-between h-full px-6 relative z-10">
      <div className="flex items-center space-x-4">
        <div className="relative overflow-hidden bg-purple-100 rounded h-6 w-6">
          <Shimmer />
        </div>
        <div className="space-y-2">
          <div className="relative overflow-hidden bg-purple-100 rounded h-5 w-32">
            <Shimmer />
          </div>
          <div className="relative overflow-hidden bg-purple-50 rounded h-4 w-24">
            <Shimmer />
          </div>
        </div>
      </div>
      <div className="relative overflow-hidden bg-purple-100 rounded-full h-10 w-10">
        <Shimmer />
      </div>
    </div>
  </header>
);

// Content skeleton loader
const ContentSkeleton = ({ variant = 'default' }) => {
  if (variant === 'cards') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
        {[...Array(6)].map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.4 }}
            className="relative overflow-hidden bg-white rounded-xl shadow-sm border border-purple-100 p-6 h-48"
          >
            <div className="space-y-4">
              <div className="relative overflow-hidden bg-purple-100 rounded-full h-12 w-12">
                <Shimmer />
              </div>
              <div className="relative overflow-hidden bg-purple-100 rounded h-6 w-3/4">
                <Shimmer />
              </div>
              <div className="relative overflow-hidden bg-purple-50 rounded h-4 w-1/2">
                <Shimmer />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {[...Array(4)].map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.15, duration: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-purple-100 p-6 space-y-3"
        >
          <div className="relative overflow-hidden bg-purple-100 rounded h-6 w-1/3">
            <Shimmer />
          </div>
          <div className="relative overflow-hidden bg-purple-100 rounded h-4 w-full">
            <Shimmer />
          </div>
          <div className="relative overflow-hidden bg-purple-50 rounded h-4 w-5/6">
            <Shimmer />
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Floating dots loading indicator
const FloatingDots = () => (
  <div className="flex items-center justify-center space-x-2 p-8">
    {[0, 1, 2].map((index) => (
      <motion.div
        key={index}
        className="w-3 h-3 rounded-full bg-[#6F00FF]"
        animate={{
          y: [0, -15, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 0.6,
          repeat: Infinity,
          delay: index * 0.2,
          ease: 'easeInOut',
        }}
      />
    ))}
  </div>
);

export default function Layout({ children, onLogout, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const handleSidebarMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setSidebarHovered(true);
  };

  const handleSidebarMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    hoverTimeoutRef.current = setTimeout(() => setSidebarHovered(false), 100);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const sidebarExpanded = sidebarOpen || sidebarHovered;
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Filter navigation items based on user role
  const navigationItems = allNavigationItems.filter(item =>
    item.roles.includes(user?.role || 'counselor')
  );

  // Initial page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Page transition effect
  useEffect(() => {
    setIsPageTransitioning(true);
    const timer = setTimeout(() => {
      setIsPageTransitioning(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleLogout = () => {
    const sessionId = localStorage.getItem('session_id');
    if (sessionId) {
      api.post('/auth/session/logout', { sessionId }).catch(() => {});
      localStorage.removeItem('session_id');
    }
    toast.success('Logged out successfully');
    onLogout();
  };

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  /* ── Session heartbeat — ping backend every 60 s ─────────────────────── */
  useEffect(() => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) return;
    const ping = () => api.post('/auth/session/heartbeat', { sessionId }).catch(() => {});
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, []);

  /* ── Enquiry share dialog ─────────────────────────────────────────────── */
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail,      setShareEmail]      = useState('');
  const [shareRecipient,  setShareRecipient]  = useState('');
  const [shareSending,    setShareSending]    = useState(false);
  const [copied,          setCopied]          = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(ENQUIRY_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!shareEmail.trim()) { toast.error('Please enter an email address'); return; }
    setShareSending(true);
    try {
      await api.post('/auth/share-enquiry', { email: shareEmail.trim(), recipientName: shareRecipient.trim() || undefined });
      toast.success(`Enquiry link sent to ${shareEmail}`);
      setShareEmail('');
      setShareRecipient('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send. Please try again.');
    } finally {
      setShareSending(false);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    handleLogout();
  };

  /* ── My Profile dialog ─────────────────────────────────────────────────── */
  const [showProfileDialog, setShowProfileDialog]   = useState(false);
  const [profileData,       setProfileData]         = useState(null);
  const [profileLoading,    setProfileLoading]      = useState(false);

  const openProfile = async () => {
    setShowProfileDialog(true);
    setProfileLoading(true);
    try {
      const res = await api.get('/auth/profile');
      setProfileData(res.data.user);
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  /* ── Change Password dialog ────────────────────────────────────────────── */
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, newPw: false, confirm: false });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwForm.newPw.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setPwLoading(true);
    try {
      await api.patch('/auth/profile/password', { currentPassword: pwForm.current, newPassword: pwForm.newPw });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  // Show initial loading screen
  if (isInitialLoading) {
    return (
      <div className="h-screen flex bg-gray-50 overflow-hidden">
        <SidebarSkeleton />
        <div className="flex-1 flex flex-col overflow-hidden">
          <HeaderSkeleton />
          <main className="flex-1 overflow-y-auto">
            <ContentSkeleton variant="cards" />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-50 overflow-hidden relative">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"></div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sidebar - spring width animation for smooth expand/collapse */}
      <motion.div
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        className={`fixed inset-y-0 left-0 z-[60] flex h-full min-h-0 flex-col overflow-hidden pt-4 pb-3 scrollbar-hidden no-scrollbar lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        initial={false}
        animate={{ width: sidebarExpanded ? 320 : 80 }}
        transition={{
          type: 'spring',
          stiffness: 280,
          damping: 34,
          mass: 0.65,
        }}
        style={{
          willChange: 'width',
          background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
          boxShadow: '4px 0 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)',
          borderRight: '1px solid rgba(148, 163, 184, 0.1)',
        }}
      >
        <div
          className={`relative z-10 shrink-0 border-b border-white/[0.08] ${
            sidebarExpanded
              ? 'flex min-h-0 flex-col items-center justify-center px-3 py-3'
              : 'flex min-h-0 items-center justify-center px-1.5 py-2.5'
          }`}
        >
          <button
            data-testid="close-sidebar-button"
            className="absolute right-2 top-2 z-20 rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-white/10 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          {/* Logo: white panel + light ring so it reads clearly on the dark sidebar */}
          <div
            className={`flex w-full min-w-0 items-center justify-center ${
              sidebarExpanded ? 'px-1' : 'px-0'
            }`}
          >
            <div
              className={`flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200/90 bg-white/95 shadow-sm ring-1 ring-black/[0.06] ${
                sidebarExpanded
                  ? 'max-w-[min(188px,calc(100%-0.5rem))] px-2.5 py-1'
                  : 'inline-flex max-w-[calc(100%-2px)] shrink-0 px-0.5 py-px'
              }`}
            >
              <motion.img
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                src="/logo.png"
                alt="Institute Manager Logo"
                className={`object-contain object-center ${
                  sidebarExpanded
                    ? 'h-9 w-auto max-w-[168px]'
                    : 'h-8 w-auto max-h-8 max-w-[72px] min-w-0'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Navigation with flex layout */}
        <nav className="relative z-10 flex min-h-0 flex-1 flex-col">
          {/* Navigation Items */}
          <div
            className={`flex-1 mt-10 overflow-y-auto ${sidebarExpanded ? 'px-4' : 'px-2'}`}
          >
            <div className="space-y-1">
              {navigationItems.map((item, index) => {
                const isActive = location.pathname === item.href;
                return (
                  <motion.button
                    key={item.name}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    onClick={() => {
                      navigate(item.href);
                      setSidebarOpen(false);
                    }}
                    className={`group/nav relative z-10 flex w-full items-center rounded-xl text-base transition-all duration-300 gap-0 px-2 py-2 ${
                      sidebarExpanded ? 'min-h-[44px]' : 'min-h-10'
                    } ${
                      isActive
                        ? 'font-semibold text-white shadow-[0_4px_14px_-2px_rgba(99,102,241,0.45)] ring-1 ring-white/15'
                        : 'font-medium text-slate-300 hover:text-white'
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Active backgrounds (behind content) */}
                    {isActive && (
                      <>
                        <div
                          className="pointer-events-none absolute inset-0 -z-10 rounded-xl opacity-80"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(129,140,248,0.28) 0%, rgba(139,92,246,0.2) 100%)',
                            filter: 'blur(8px)',
                          }}
                          aria-hidden
                        />
                        <motion.div
                          layoutId="activeBackground"
                          className="absolute inset-0 -z-10 rounded-xl bg-gradient-to-br from-indigo-500 via-violet-600 to-indigo-700 shadow-inner"
                          style={{
                            boxShadow:
                              'inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.12), 0 2px 8px rgba(79,70,229,0.35)',
                          }}
                          initial={false}
                          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                        />
                        <div
                          className="pointer-events-none absolute left-2 right-2 top-0 z-0 h-px rounded-full bg-gradient-to-r from-transparent via-white/35 to-transparent"
                          aria-hidden
                        />
                      </>
                    )}

                    {!isActive && (
                      <div className="absolute inset-0 rounded-xl bg-white/[0.06] opacity-0 transition-opacity duration-300 group-hover/nav:opacity-100" />
                    )}

                    {/* Collapsed: stick is absolute on left edge — icon stays truly centered (no overlap) */}
                    {!sidebarExpanded && isActive && (
                      <span
                        className="pointer-events-none absolute left-0 top-1/2 z-20 h-8 w-1 -translate-y-1/2 rounded-r-md bg-gradient-to-b from-cyan-100 via-white to-violet-400 shadow-[0_0_12px_rgba(165,243,252,0.75)] ring-1 ring-white/25"
                        aria-hidden
                      />
                    )}

                    {sidebarExpanded ? (
                      <div className="relative z-10 flex w-full min-w-0 items-center gap-2 pl-1 pr-1">
                        <span
                          className={`shrink-0 rounded-full ${
                            isActive
                              ? 'h-8 w-1.5 bg-gradient-to-b from-cyan-100 via-white to-violet-400 shadow-[0_0_14px_rgba(165,243,252,0.75)] ring-1 ring-white/25'
                              : 'invisible h-8 w-1.5'
                          }`}
                          aria-hidden
                        />
                        <item.icon
                          className={`shrink-0 h-5 w-5 ${
                            isActive ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]' : 'text-slate-400'
                          }`}
                        />
                        <span
                          className={`min-w-0 flex-1 text-left leading-tight tracking-tight ${
                            isActive ? 'drop-shadow-sm' : ''
                          }`}
                        >
                          {item.name}
                        </span>
                      </div>
                    ) : (
                      <div className="relative z-10 flex h-10 w-full items-center justify-center px-1">
                        <item.icon
                          className={`h-6 w-6 shrink-0 ${
                            isActive ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]' : 'text-slate-400'
                          }`}
                        />
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* ── Enquiry Form shortcut ─────────────────────────────────────────── */}
          <div className={`border-t border-white/10 pt-2 ${sidebarExpanded ? 'px-4' : 'px-2'}`}>
            {sidebarExpanded ? (
              <div className="rounded-xl overflow-hidden border border-violet-400/20 bg-violet-500/10">
                <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                  <QrCodeIcon className="h-4 w-4 text-violet-300 shrink-0" />
                  <span className="text-xs font-semibold text-violet-200 truncate">Public Enquiry Form</span>
                </div>
                <div className="flex gap-2 px-3 pb-3 pt-1">
                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    onClick={() => window.open(ENQUIRY_URL, '_blank', 'noreferrer')}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-violet-500/20 hover:bg-violet-500/35 text-violet-200 text-xs font-medium transition-colors"
                  >
                    <ShareIcon className="h-3.5 w-3.5" />
                    Open
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    onClick={() => setShowShareDialog(true)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-orange-500/20 hover:bg-orange-500/35 text-orange-200 text-xs font-medium transition-colors"
                  >
                    <EnvelopeIcon className="h-3.5 w-3.5" />
                    Share
                  </motion.button>
                </div>
              </div>
            ) : (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowShareDialog(true)}
                title="Share Enquiry Form"
                className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl border border-violet-400/30 bg-violet-500/20 text-violet-300 hover:bg-violet-500/35 transition-colors"
              >
                <QrCodeIcon className="h-5 w-5" />
              </motion.button>
            )}
          </div>

          {/* User Role Badge */}
          {sidebarExpanded ? (
            <div className="px-3 pb-2 pt-1.5">
              <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Current role
                </p>
                <p className="mt-1.5 text-base font-semibold capitalize leading-snug text-slate-100">
                  {user?.role || 'Counselor'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center px-2 pb-2 pt-0.5">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-indigo-400/30 bg-indigo-500/30"
                title={`Role: ${user?.role || 'Counselor'}`}
              >
                <span className="text-sm font-semibold leading-none text-indigo-200">
                  {(user?.role || 'Counselor').charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          )}

          {/* Sign Out Button */}
          <motion.div
            className={`${sidebarExpanded ? 'px-3 pb-4 pt-2' : 'px-2 pb-3 pt-1.5'} border-t border-white/10`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              delay: 0.3,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          >
            {sidebarExpanded ? (
              <motion.button
                onClick={handleLogoutClick}
                className="group flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-2.5 text-sm font-medium text-red-300 transition-all duration-200 hover:bg-red-500/20"
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2, ease: 'easeOut' },
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-red-400/30 bg-red-500/20">
                  <ArrowRightOnRectangleIcon className="h-6 w-6 transition-transform group-hover:scale-105" />
                </span>
                Sign Out
              </motion.button>
            ) : (
              <motion.button
                onClick={handleLogoutClick}
                className="mx-auto flex h-10 w-10 min-w-[40px] items-center justify-center rounded-full border border-red-400/35 bg-red-500/15 text-red-300 shadow-inner transition-colors hover:bg-red-500/25"
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.2, ease: 'easeOut' },
                }}
                whileTap={{ scale: 0.95 }}
                title="Sign out"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </motion.button>
            )}
          </motion.div>
        </nav>
      </motion.div>

      {/* Logout Confirmation Dialog */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: 'spring',
                damping: 25,
                stiffness: 300,
              }}
            >
              <div className="text-center">
                <motion.div
                  className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-50 mb-4 border-2 border-red-100"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    delay: 0.1,
                    type: 'spring',
                    stiffness: 260,
                    damping: 20,
                  }}
                >
                  <ArrowRightOnRectangleIcon className="h-7 w-7 text-red-600" />
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Sign Out</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to sign out of your account?
                </p>
                <div className="flex gap-3 justify-center">
                  <motion.button
                    type="button"
                    onClick={() => setShowLogoutConfirm(false)}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={confirmLogout}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all shadow-lg shadow-red-500/30"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Sign Out
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── My Profile Dialog ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {showProfileDialog && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowProfileDialog(false)}
          >
            <motion.div
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold">
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-base">My Profile</p>
                    <p className="text-violet-200 text-xs">Account information</p>
                  </div>
                </div>
                <button onClick={() => setShowProfileDialog(false)} className="text-white/70 hover:text-white">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              {/* Body */}
              <div className="p-6 space-y-4">
                {profileLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="h-7 w-7 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin" />
                  </div>
                ) : profileData ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Full Name</p>
                        <p className="text-sm font-semibold text-gray-800">{profileData.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Role</p>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700 capitalize">{profileData.role}</span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="text-sm font-medium text-gray-800 break-all">{profileData.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Phone</p>
                        <p className="text-sm font-medium text-gray-800">{profileData.phone || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${profileData.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{profileData.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Member Since</p>
                        <p className="text-sm font-medium text-gray-800">{profileData.createdAt ? new Date(profileData.createdAt).toLocaleDateString() : '—'}</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <button
                        onClick={() => { setShowProfileDialog(false); setShowPasswordDialog(true); setPwForm({ current: '', newPw: '', confirm: '' }); }}
                        className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 font-medium"
                      >
                        <KeyIcon className="h-4 w-4" />
                        Change Password
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-6">Could not load profile data.</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Change Password Dialog ──────────────────────────────────────────── */}
      <AnimatePresence>
        {showPasswordDialog && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowPasswordDialog(false)}
          >
            <motion.div
              className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <LockClosedIcon className="h-5 w-5" />
                  <p className="font-semibold text-base">Change Password</p>
                </div>
                <button onClick={() => setShowPasswordDialog(false)} className="text-white/70 hover:text-white">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleChangePassword} className="p-6 space-y-4">
                {[
                  { id: 'current', label: 'Current Password', key: 'current' },
                  { id: 'new',     label: 'New Password',     key: 'newPw'   },
                  { id: 'confirm', label: 'Confirm New Password', key: 'confirm' },
                ].map(({ id, label, key }) => (
                  <div key={id} className="space-y-1.5">
                    <label htmlFor={`pw-${id}`} className="text-xs font-medium text-gray-700">{label}</label>
                    <div className="relative">
                      <input
                        id={`pw-${id}`}
                        type={showPw[key] ? 'text' : 'password'}
                        value={pwForm[key]}
                        onChange={(e) => setPwForm(prev => ({ ...prev, [key]: e.target.value }))}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                        placeholder={label}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw(prev => ({ ...prev, [key]: !prev[key] }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPw[key] ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-500">Password must be at least 6 characters.</p>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordDialog(false)}
                    className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={pwLoading}
                    className="flex-1 py-2.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {pwLoading && <div className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                    {pwLoading ? 'Changing…' : 'Change Password'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Share Enquiry Dialog ────────────────────────────────────────────── */}
      <AnimatePresence>
        {showShareDialog && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowShareDialog(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg,#1c2b4a,#2d3e6d)' }}>
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                    <QrCodeIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Share Enquiry Form</p>
                    <p className="text-xs text-blue-200">Public form — no login required</p>
                  </div>
                </div>
                <button onClick={() => setShowShareDialog(false)} className="rounded-lg p-1.5 text-white/70 hover:bg-white/15 transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-5">
                {/* QR Code */}
                <div className="flex flex-col items-center gap-3">
                  <div className="rounded-xl border-2 border-gray-100 bg-white p-4 shadow-sm">
                    <QRCodeSVG
                      value={ENQUIRY_URL}
                      size={160}
                      bgColor="#ffffff"
                      fgColor="#1c2b4a"
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center">Scan this QR code to open the enquiry form</p>
                </div>

                {/* URL + Copy */}
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Form Link</Label>
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="flex-1 text-sm text-gray-700 truncate">{ENQUIRY_URL}</span>
                    <motion.button
                      whileTap={{ scale: 0.93 }}
                      onClick={handleCopyLink}
                      className={`flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${copied ? 'bg-green-100 text-green-700' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                    >
                      {copied ? <><CheckIcon className="h-3.5 w-3.5" />Copied!</> : <><ClipboardDocumentIcon className="h-3.5 w-3.5" />Copy</>}
                    </motion.button>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400 font-medium">or send via email</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Email Form */}
                <form onSubmit={handleSendEmail} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Recipient Name</Label>
                      <Input
                        placeholder="e.g. Rahul"
                        value={shareRecipient}
                        onChange={(e) => setShareRecipient(e.target.value)}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Email Address *</Label>
                      <Input
                        type="email"
                        placeholder="student@example.com"
                        value={shareEmail}
                        onChange={(e) => setShareEmail(e.target.value)}
                        required
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                  <motion.button
                    type="submit"
                    disabled={shareSending || !shareEmail.trim()}
                    whileHover={{ scale: shareSending ? 1 : 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg,#e05b1a,#f5a623)' }}
                  >
                    <EnvelopeIcon className="h-4 w-4" />
                    {shareSending ? 'Sending…' : 'Send Enquiry Link'}
                  </motion.button>
                </form>

                {/* Open in browser */}
                <button
                  onClick={() => { window.open(ENQUIRY_URL, '_blank', 'noreferrer'); setShowShareDialog(false); }}
                  className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <ShareIcon className="h-4 w-4" />
                  Open Form in New Tab
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-purple-100 top-0 z-30 relative overflow-hidden" style={{
          background: 'linear-gradient(30deg, rgba(99, 102, 241, 0.08), transparent)'
        }}>
          <div className="flex items-center justify-between h-16 px-6 relative z-10">
            <div className="flex items-center">
              <button
                data-testid="open-sidebar-button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden mr-4 p-2 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <Bars3Icon className="h-6 w-6 text-purple-600" />
              </button>

              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-lg font-semibold text-gray-900">
                  {navigationItems.find((item) => item.href === location.pathname)?.name ||
                    'Dashboard'}
                </h2>
                <p className="text-sm text-gray-600">
                  Welcome back, {user?.name || 'Admin'}
                </p>
              </motion.div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="user-menu-button"
                  variant="ghost"
                  className="relative h-10 w-10 rounded-full ring-2 ring-purple-200 hover:ring-purple-300 transition-all bg-purple-50"
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="h-full w-full rounded-full flex items-center justify-center"
                  >
                    <span className="font-semibold text-lg text-[#6F00FF]">
                      {(user?.name || 'A').charAt(0).toUpperCase()}
                    </span>
                  </motion.div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64" align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.name || 'Administrator'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || 'admin@edumanager.com'}
                    </p>
                    <div className="mt-2 inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-[#6F00FF] text-xs font-medium w-fit">
                      <span className="capitalize">{user?.role || 'Admin'}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={openProfile}
                  className="cursor-pointer focus:bg-violet-50"
                >
                  <UserCircleIcon className="mr-2 h-4 w-4 text-violet-500" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setShowPasswordDialog(true); setPwForm({ current: '', newPw: '', confirm: '' }); }}
                  className="cursor-pointer focus:bg-violet-50"
                >
                  <KeyIcon className="mr-2 h-4 w-4 text-violet-500" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer focus:text-red-700 focus:bg-red-50"
                >
                  <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {isPageTransitioning ? (
              <motion.div
                key="loading"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="p-6"
              >
                <FloatingDots />
                <ContentSkeleton variant="default" />
              </motion.div>
            ) : (
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="p-6"
              >
                {children}
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Page transition overlay */}
      <AnimatePresence>
        {isPageTransitioning && (
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            exit={{ scaleX: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed top-0 left-0 right-0 h-1 bg-[#6F00FF] origin-left z-50"
          >
            <motion.div
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 0.6, repeat: Infinity, ease: 'linear' }}
              className="h-full w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}